import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { ctaConfig as defaultCtaConfig } from '@/config';
import { useConfig } from '@/lib/siteData';
import { trackEvent } from '@/lib/plausible';

function NewsletterInlineForm() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setError('Please enter a valid email');
      return;
    }
    setError(null);
    setSubmitting(true);

    // Persist through the existing /api/leads endpoint so subscribers land
    // in the admin Leads panel and any configured notification channels
    // (Telegram, Resend, SMTP) fire via the standard dispatchNotifications
    // fan-out. `source: 'newsletter_cta'` discriminates from contact-form
    // and chat-widget rows; the LeadsManager surfaces it in the source
    // column. fullName is a synthetic placeholder because the schema
    // requires `min(1)` and we don't collect names at this surface.
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          fullName: 'Newsletter Subscriber',
          email,
          consent: true,
          source: 'newsletter_cta',
        }),
      });
      if (!res.ok) {
        if (res.status === 429) {
          setError('Too many subscriptions from this network. Try again later.');
        } else {
          setError('Subscription failed. Please try again later.');
        }
        return;
      }
      setSubmitted(true);
      trackEvent('Newsletter Subscribe', { location: 'cta' });
    } catch {
      setError('Subscription failed. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <p className="inline-flex items-center gap-2 text-sm text-green-600 font-body">
        <Check className="w-4 h-4" />
        Subscribed. Thank you!
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full" noValidate>
      <div className="flex flex-col gap-2">
        <label htmlFor="cta-email" className="sr-only">
          Email address<span className="text-accent-blue" aria-hidden="true"> *</span>
        </label>
        <input
          id="cta-email"
          name="email"
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          placeholder="your@email.com"
          aria-invalid={!!error}
          aria-describedby={error ? 'cta-email-error' : undefined}
          className="w-full rounded-lg border border-surface-border-gray bg-surface-elevated px-4 py-3 text-sm text-text-primary placeholder:text-text-light focus:outline-none focus:border-accent-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue transition-colors"
        />
        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent-blue hover:bg-accent-blue-hover px-5 py-2.5 text-sm font-medium text-accent-fg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
          {submitting ? 'Subscribing…' : 'Subscribe'}
        </button>
      </div>
      {error && (
        <p id="cta-email-error" className="mt-2 text-xs text-red-600 font-body text-left">
          {error}
        </p>
      )}
    </form>
  );
}

export function CTA({ index }: { index?: number } = {}) {
  const ctaConfig = useConfig('cta_config', defaultCtaConfig);
  if (ctaConfig.visible === false) return null;

  return (
    <section id="contact" className="w-full py-16 md:py-20 bg-surface-gray border-t border-surface-border-gray">
      <div className="w-full max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <span className="font-mono-label text-xs uppercase tracking-[0.2em] text-text-light">
            {index != null && <span className="text-accent-blue mr-1">{String(index).padStart(2, '0')} — </span>}
            GET IN TOUCH
          </span>
          <h2 className="font-geist-mono text-3xl md:text-[2.5rem] font-medium text-text-primary mt-3 leading-[1.1] tracking-tight">
            {ctaConfig.heading}
          </h2>
          <p className="font-body text-base text-text-gray mt-3 max-w-xl mx-auto whitespace-pre-line">
            {ctaConfig.body || "Consulting, speaking, thought leadership, or mentorship — pick how you'd like to engage."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          <div className="flex flex-col text-center p-6 rounded-xl border border-surface-border-gray bg-surface-elevated">
            <h3 className="font-heading text-base font-semibold text-text-primary mb-2">
              Work with me
            </h3>
            <p className="font-body text-sm text-text-gray leading-relaxed mb-5 flex-1">
              Consulting, advisory, speaking, or mentorship engagements.
            </p>
            <Link
              to="/contact"
              onClick={() => trackEvent('Book Consultation Click')}
              className="inline-flex w-full items-center justify-center px-5 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-accent-fg rounded-lg text-sm font-medium transition-colors"
            >
              Book a consultation →
            </Link>
          </div>

          <div
            data-newsletter-anchor
            className="flex flex-col text-center p-6 rounded-xl border border-surface-border-gray bg-surface-elevated"
          >
            <h3 className="font-heading text-base font-semibold text-text-primary mb-2">
              Get new insights first
            </h3>
            <p className="font-body text-sm text-text-gray leading-relaxed mb-5 flex-1">
              Articles on FinTech, payments, and leadership — direct to your inbox.
            </p>
            <NewsletterInlineForm />
          </div>

          <div className="flex flex-col text-center p-6 rounded-xl border border-surface-border-gray bg-surface-elevated">
            <h3 className="font-heading text-base font-semibold text-text-primary mb-2">
              Follow along
            </h3>
            <p className="font-body text-sm text-text-gray leading-relaxed mb-5 flex-1">
              Connect on LinkedIn for regular updates and commentary.
            </p>
            <a
              href="https://www.linkedin.com/in/chinedukoggu/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('LinkedIn Follow Click')}
              className="inline-flex w-full items-center justify-center px-5 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-accent-fg rounded-lg text-sm font-medium transition-colors"
            >
              Follow on LinkedIn →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
